/**
 * Wie mag wat in het subsidiebeheer, en wat betekent het als de database nee
 * zegt.
 *
 * ── HET BESLUIT VAN 12-09-2026 ────────────────────────────────────────────
 *
 * Ronde één van T7.4 probeerde de schade te voorkomen door kolommen op slot te
 * zetten: `waarde` vrij, `veld` en `operator` op slot; `bedrag` vrij,
 * `categorie` en `bedrag_type` op slot. Die grens is afgekeurd omdat hij niet
 * werkt. De scepticus mat met de echte rekenkern en de echte rijen dat de drie
 * kolommen die "vrij" heetten een klantofferte stil verhogen:
 *
 *   sub_regels.waarde    EUR 0 -> EUR 212.000 op intake 6daab7ab
 *   sub_bedragen.bedrag  EUR 89.505 -> EUR 112.590 (een ándere aftrekschijf wint)
 *   max_bedrag = 0       de rij verhuist onder het gedeelde plafond
 *
 * Een slot op de kolomNAAM kan dat niet zien. Een voorvertoning op de
 * UITKOMST wel. Stan heeft daarom besloten: alles vrij, maar bij een wijziging
 * die een offertebedrag verschuift laat het scherm eerst zien wát er
 * verschuift, en pas na een aparte bevestiging wordt er geschreven. Die
 * voorvertoning staat in `subsidie-voorvertoning.ts` en is de eigenlijke
 * bescherming; dit bestand gaat alleen nog over ROLLEN en over de vraag wat een
 * weigering van de database betekent.
 *
 * Wat er van de kolomsloten overblijft is IDENTITEIT: `id`, `created_at`, op de
 * twee kindtabellen `regeling_id`, en op `sub_regelingen` ook `code` en `jaar`.
 * Een regel naar een andere regeling verplaatsen is geen correctie maar een
 * verhuizing; een regeling naar een ander jaar verplaatsen haalt hem mét al
 * zijn regels en bedragen uit elke lopende berekening.
 *
 * Stan heeft op diezelfde dag nog twee dingen vrijgegeven die eerst dicht
 * stonden: een crm_user mag nu regels en bedragen VERWIJDEREN (met verplichte
 * bevestiging, en de voorvertoning zegt erbij dat het onomkeerbaar is) en een
 * nieuwe REGELING aanmaken (waar de voorvertoning niets kan tonen, en het
 * scherm dat zegt in plaats van een lege tabel op te hangen). Alleen een hele
 * regeling weggooien blijft bij een beheerder: daar gaat via ON DELETE CASCADE
 * alles onder mee.
 *
 * ── DE BRON VAN DE WAARHEID IS DE DATABASE, NIET DIT BESTAND ──────────────
 *
 * Wat hieronder staat is een KOPIE van wat de policies en het kolomslot
 * toestaan. Wie deze lijst ruimer maakt dan de database, krijgt geen gat maar
 * een foutmelding. De test legt beide naast elkaar:
 * `docs/laadplein-designer/migrations/083_*.forward.sql`.
 *
 * Bewust vrij van React, Supabase en de `@/`-alias, zodat
 * `tests/subsidie/beheer-schrijfrecht.node.test.mjs` het rechtstreeks kan laden
 * onder `node --test`.
 */

/** De rol zoals `useAuth().userRole` hem geeft: precies één, of geen. */
export type BeheerRol = string | null | undefined;

/**
 * Wat er werkelijk in de database staat, gemeten in plaats van aangenomen.
 *
 * Dit bestand beschrijft twee standen tegelijk: die van 070 (vandaag toegepast)
 * en die van 083 (geschreven, nog niet toegepast). Zonder deze vlag zou het
 * scherm een crm_user rechten beloven die hij pas ná de migratie heeft — en dat
 * is precies wat een banner niet mag doen. `useSubsidieSchemaStand` meet het
 * met één SELECT.
 */
export interface SchemaStand {
  /** Staat `updated_at` op `sub_regels`? Dan draait 083. */
  migratie083: boolean;
}

/** De drie rollen die 062, 070, 081, 082 en 083 als "het CRM-team" behandelen. */
export const CRM_ROLLEN: readonly string[] = ['admin', 'super_admin', 'crm_user'];

/** De twee rollen die verwijderen en nieuwe regelingen aanmaken. */
export const CATALOGUS_ROLLEN: readonly string[] = ['admin', 'super_admin'];

/* ──────────────────────────────────────────────────────────────────────────
 * De kolommen, per tabel, zoals het kolomslot van 083 ze kent.
 *
 * Drie categorieën in plaats van twee:
 *
 *   IDENTITEIT   wie deze rij IS en waar hij hoort. Niet te wijzigen, door
 *                niemand via het scherm. Geen invoerveld.
 *   AUDIT        door de trigger gezet, wat de client ook meestuurt. Niet te
 *                vervalsen — dat is een lock door overschrijven, niet door
 *                weigeren.
 *   VRIJ         al het overige: elke inhoudelijke kolom. Dit is het besluit.
 *
 * De test legt deze drie naast de `.forward.sql` én naast de gemeten
 * kolomlijsten. Wijkt er één af, dan zou het scherm een veld openzetten dat bij
 * opslaan met 42501 terugkomt — of, erger, een veld dichtzetten dat gewoon mocht.
 * ────────────────────────────────────────────────────────────────────────── */

/** Door de trigger gezet, nooit door de client. Op alle drie de tabellen. */
export const AUDITKOLOMMEN: readonly string[] = ['bijgewerkt_door', 'updated_at'];

/**
 * `code` en `jaar` staan hier op besluit van Stan (12-09-2026), tégen mijn
 * advies en dat van de scepticus in. Het argument vóór vrijlaten is dat RVO
 * codes hernoemt en dat dat dan een migratie kost. De argumenten waarop Stan
 * ze tóch dichtzet zijn zwaarder gemeten:
 *
 *   jaar   één regeling naar 2027 verhuizen haalt hem MÉT zijn regels en
 *          bedragen uit de 2026-stand. Gemeten: 6daab7ab EIA EUR 102.600 -> 0
 *          en MIA EUR 89.505 -> 0, 717e4678 FLEX_E EUR 36.000 -> 0 en AANZET
 *          EUR 23.400 -> 0. De rekenkern leest `jaar` nergens, dus de
 *          voorvertoning meldt "geen verschil" — het gat zit in de LEESKANT,
 *          die op jaar sleutelt. Het schermpad was toevallig dicht (het veld
 *          stond op `disabled`), de database wagenwijd open.
 *   code   `evaluateAllSubsidies` matcht er letterlijk op voor de EIA/MIA-
 *          stapelwaarschuwing, de enige combinatietoets in de hele engine.
 *
 * Dat `code` nu op slot staat is GEEN vervanging van de echte reparatie: de
 * voorvertoning vergelijkt sinds vandaag ook `match_status` en `aanbevelingen`,
 * dus een verdwijnende waarschuwing wordt hoe dan ook getoond. Zie
 * `subsidie-voorvertoning.ts`.
 */
export const REGELING_IDENTITEIT: readonly string[] = ['id', 'created_at', 'code', 'jaar'];
export const REGEL_IDENTITEIT: readonly string[] = ['id', 'created_at', 'regeling_id'];
export const BEDRAG_IDENTITEIT: readonly string[] = ['id', 'created_at', 'regeling_id'];

/**
 * Wat een crm_user van een REGELING wél mag wijzigen.
 *
 * `budget_teller` staat hier nadrukkelijk bij. Migratie 079 zette die kolom ná
 * 070 op de tabel, en het kolomslot van 070 was een whitelist — alles wat er
 * later bij kwam stond daardoor vanzelf op slot, zonder dat iemand dat besloot.
 * 083 zet dat recht, en draait de vorm om: een blacklist op identiteit, zodat
 * een kolom die er straks bij komt standaard VRIJ is en niet stil dicht. Dat is
 * de juiste verstek onder het nieuwe besluit — wat geld verschuift wordt
 * gevangen door de voorvertoning, niet door de kolomnaam.
 */
export const REGELING_VRIJ: readonly string[] = [
  'naam', 'korte_naam', 'type', 'status',
  'aanvraag_open', 'aanvraag_sluit', 'concept_vanaf',
  'budget_totaal', 'budget_restant', 'budget_toelichting', 'budget_teller',
  'max_subsidie_per_aanvrager', 'min_subsidiebedrag', 'subsidie_percentage_max',
  'rvo_url', 'opmerkingen', 'samenvatting', 'bron_gecontroleerd_op',
];

/**
 * De whitelist die 070 VANDAAG in de database heeft staan — niet wat 083 ervan
 * maakt.
 *
 * Letterlijk overgenomen uit `070_subsidie_schrijfrecht.forward.sql:207-214`.
 * Zolang 083 niet is toegepast, is dít wat een crm_user van een regeling mag
 * wijzigen. Vijf kolommen die `REGELING_VRIJ` vrij noemt staan er NIET in —
 * `type`, `budget_teller`, `max_subsidie_per_aanvrager`, `min_subsidiebedrag`
 * en `subsidie_percentage_max` — en die kwamen bij opslaan terug met 42501
 * terwijl het formulier ze openzette. Het scherm hoort de werkelijke stand te
 * kennen in plaats van aan te nemen dat de migratie al draait.
 */
export const REGELING_VRIJ_070: readonly string[] = [
  'naam', 'korte_naam', 'status',
  'aanvraag_open', 'aanvraag_sluit', 'concept_vanaf',
  'budget_totaal', 'budget_restant', 'budget_toelichting',
  'rvo_url', 'opmerkingen', 'samenvatting', 'bron_gecontroleerd_op',
];

/** Wat een crm_user van een REGEL wél mag wijzigen: alles inhoudelijks. */
export const REGEL_VRIJ: readonly string[] = [
  'veld', 'operator', 'waarde', 'verplicht',
  'beschrijving', 'foutmelding', 'groep', 'sort_order',
];

/** Wat een crm_user van een BEDRAG wél mag wijzigen: alles inhoudelijks. */
export const BEDRAG_VRIJ: readonly string[] = [
  'categorie', 'subcategorie', 'bedrag_type', 'bedrag',
  'max_bedrag', 'max_eenheden', 'min_eenheden', 'bedrijfsgrootte',
  'opmerkingen', 'sort_order',
];

/* ──────────────────────────────────────────────────────────────────────────
 * De rechten
 * ────────────────────────────────────────────────────────────────────────── */

export interface Schrijfrechten {
  /** Mag deze rol überhaupt iets schrijven in dit scherm? */
  ietsSchrijven: boolean;

  /**
   * Een nieuwe regeling aanmaken — policy sub_regelingen_insert_beheer (083).
   *
   * 070 liet die bewust weg ("een regeling is een nieuwe rekengrondslag, geen
   * correctie"); Stan heeft dat op 12-09-2026 teruggedraaid. Let op wat die
   * keuze kost: de voorvertoning kán een nieuwe regeling niet doorrekenen, want
   * er staan nog geen regels en bedragen onder. Het scherm zegt dat met zoveel
   * woorden in plaats van een lege tabel te tonen.
   */
  regelingAanmaken: boolean;
  /** Een bestaande regeling bijwerken — policy sub_regelingen_update_beheer (070). */
  regelingBijwerken: boolean;
  /**
   * Een regeling verwijderen — cascadeert naar regels en bedragen (070).
   * Blijft bij admin: Stan gaf DELETE vrij op de twee KINDtabellen, niet op de
   * regeling zelf, en het verschil is groot — hier gaan 15 regels en 46
   * bedragen in één klik mee.
   */
  regelingVerwijderen: boolean;
  /**
   * De code en het jaar van een BESTAANDE regeling wijzigen — de twee kolommen
   * die 083 als identiteit bewaakt. Alleen admin en super_admin; bij een NIEUWE
   * regeling staat alles open, want een INSERT heeft geen OLD om tegen te
   * vergelijken. Zie `REGELING_IDENTITEIT` voor de meting erachter.
   */
  regelingIdentiteit: boolean;
  /**
   * De rekengrondslag van een regeling: `type`, `budget_teller`,
   * `max_subsidie_per_aanvrager`, `min_subsidiebedrag` en
   * `subsidie_percentage_max`.
   *
   * 083 geeft die vijf vrij; de whitelist van 070 die vandaag in de database
   * staat doet dat niet (`REGELING_VRIJ_070`). Zolang 083 niet is toegepast
   * staan ze dus uit voor een crm_user — anders belooft het formulier iets dat
   * bij opslaan met 42501 terugkomt.
   */
  regelingRekengrondslag: boolean;

  /** Een toetsingsregel toevoegen — policy sub_regels_insert_beheer (083). */
  regelToevoegen: boolean;
  /** Een toetsingsregel bijwerken — policy sub_regels_update_beheer (083). */
  regelBijwerken: boolean;
  /** Een toetsingsregel verwijderen — policy sub_regels_delete_beheer (083). */
  regelVerwijderen: boolean;

  /** Een bedrag toevoegen — policy sub_bedragen_insert_beheer (083). */
  bedragToevoegen: boolean;
  /** Een bedrag bijwerken — policy sub_bedragen_update_beheer (083). */
  bedragBijwerken: boolean;
  /** Een bedrag verwijderen — policy sub_bedragen_delete_beheer (083). */
  bedragVerwijderen: boolean;
}

/**
 * De redenen, in de taal van het scherm. Ze staan hier en niet in de JSX omdat
 * ze op meer dan één plek moeten kloppen: naast een uitgeschakelde knop, naast
 * een uitgeschakeld veld, en in de banner bovenaan.
 *
 * Elke zin zegt WAT er niet mag en WAT je dan doet. Een uitgeschakelde knop
 * zonder reden leert niemand iets — vandaar dat geen van deze knoppen verborgen
 * wordt.
 */
export const REDEN = {
  geenSchrijfrecht:
    'Je account mag niets wijzigen in de subsidiedata. Vraag een beheerder.',
  regelingAanmaken:
    'Een nieuwe regeling aanmaken mag alleen een beheerder: een regeling is een nieuwe rekengrondslag, geen correctie.',
  verwijderen:
    'Een hele regeling weghalen mag alleen een beheerder: daar gaan via ON DELETE CASCADE ook alle regels '
    + 'en bedragen eronder in mee. Losse regels en bedragen mag je zelf verwijderen — je ziet dan eerst wat '
    + 'het met de lopende dossiers doet. Vraag anders een beheerder.',
  identiteitRegeling:
    'De code en het jaar van een bestaande regeling wijzigen mag alleen een beheerder. Die twee bepalen '
    + 'onder welke jaargang deze regeling met al zijn regels en bedragen valt — hem naar een ander jaar '
    + 'verplaatsen haalt hem uit elke lopende berekening zonder dat de rekenkern dat merkt. Vraag een '
    + 'beheerder, of maak een nieuwe regeling aan.',
  rekengrondslag070:
    'Het soort, de budgetteller en de drie geld-grenzen van een regeling staan nog op slot: migratie 083 '
    + 'is nog niet toegepast, en het kolomslot van 070 laat ze niet door. Vraag een beheerder om de '
    + 'migratie toe te passen, of om deze velden voor je te wijzigen.',
  onomkeerbaar:
    'Verwijderen kan niet ongedaan worden gemaakt: deze tabellen kennen geen "verwijderd op" en geen '
    + 'historie. En het kan een bedrag verhógen — haal je de MIA-schijf van 27% weg, dan is 36% de laagste '
    + 'en stijgt de uitkomst. Twijfel je, zet het bedrag dan op 0 of vraag een beheerder.',
  bevestigingNodig:
    'Deze wijziging verschuift een bedrag in een lopend dossier. Bekijk wat er verandert en bevestig het daar; '
    + 'zonder die bevestiging wordt er niets opgeslagen. Twijfel je over het gevolg, vraag dan een beheerder.',
} as const;

/**
 * Het enige antwoord op "wat mag deze rol".
 *
 * `useAuth().hasRole` vergelijkt `userRole === role` (useAuth.tsx:98), dus een
 * gebruiker heeft er precies één. Een onbekende of ontbrekende rol krijgt
 * overal `false` — de veilige kant, en dezelfde als vandaag: er zijn drie
 * accounts met een profiel en een JWT maar zonder rol.
 */
export function schrijfrechtenVoorRol(rol: BeheerRol, stand: SchemaStand): Schrijfrechten {
  const isCatalogus = typeof rol === 'string' && CATALOGUS_ROLLEN.includes(rol);
  const isCrm = typeof rol === 'string' && CRM_ROLLEN.includes(rol);
  /**
   * Zonder 083 bestaan de policies op `sub_regels` en `sub_bedragen` niet en
   * geldt op `sub_regelingen` nog de smalle whitelist van 070. Een admin merkt
   * daar niets van — de ALL-policy en de admin-uitzondering in het kolomslot
   * staan er al sinds 070 — maar een crm_user loopt dan tegen 42501 of tegen
   * nul geraakte rijen aan. Het scherm belooft dat dus niet.
   */
  const crmMag = isCrm && (isCatalogus || stand.migratie083);

  return {
    ietsSchrijven: isCrm,

    regelingAanmaken: crmMag,
    // 070 gaf crm_user al UPDATE op sub_regelingen; alleen de kolomlijst is
    // smaller zonder 083 (zie `regelingRekengrondslag`).
    regelingBijwerken: isCrm,
    // De enige handeling die bij een beheerder blijft: één klik neemt 15 regels
    // en 46 bedragen mee via ON DELETE CASCADE.
    regelingVerwijderen: isCatalogus,
    regelingIdentiteit: isCatalogus,
    regelingRekengrondslag: isCrm && (isCatalogus || stand.migratie083),

    regelToevoegen: crmMag,
    regelBijwerken: crmMag,
    regelVerwijderen: crmMag,

    bedragToevoegen: crmMag,
    bedragBijwerken: crmMag,
    bedragVerwijderen: crmMag,
  };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Een geweigerde schrijfactie mag nooit als gelukt worden gemeld
 * ────────────────────────────────────────────────────────────────────────── */

export type SchrijffoutSoort =
  /** De trigger heeft een identiteitskolom geweigerd (SQLSTATE 42501, met kolomnaam). */
  | 'kolom-op-slot'
  /** RLS liet de rij niet door, mét foutmelding (42501 op een INSERT). */
  | 'rij-geweigerd'
  /** RLS liet de rij niet door, ZONDER foutmelding: nul geraakte rijen. */
  | 'niets-geschreven'
  /** De voorvertoning is niet bevestigd; er is met opzet niet geschreven. */
  | 'niet-bevestigd'
  /** De tabel of kolom bestaat niet — migratie nog niet toegepast. */
  | 'niet-toegepast'
  /** Iets anders. */
  | 'onbekend';

export interface Schrijffout {
  soort: SchrijffoutSoort;
  /** `sub_bedragen.regeling_id` als de melding hem noemt, anders null. */
  kolom: string | null;
  /** Wat het scherm toont. Begint met wat er is geweigerd, niet met "Error:". */
  melding: string;
}

/** Haalt `tabel.kolom` uit een triggermelding, of null. */
export function kolomUitMelding(melding: string): string | null {
  const m = /\b(sub_(?:regelingen|regels|bedragen))\.([a-z_]+)/.exec(melding);
  return m ? `${m[1]}.${m[2]}` : null;
}

function leesCode(error: unknown): string | null {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return null;
}

function leesMelding(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return String(error);
}

/** Onze eigen fout, gegooid door `telGeraakteRijen`. */
export class NietsGeschreven extends Error {
  readonly soort = 'niets-geschreven' as const;
  constructor(melding: string) {
    super(melding);
    this.name = 'NietsGeschreven';
  }
}

/**
 * Postgres geeft bij een UPDATE of DELETE die door RLS wordt geblokkeerd geen
 * fout: de rij valt buiten de policy en het statement raakt nul rijen. Zonder
 * deze controle zou het scherm "opgeslagen" melden terwijl er niets gebeurde.
 * Daarom vraagt elke schrijfactie `select('id')` terug en telt hier wat er
 * werkelijk is geraakt.
 *
 * Zelfde patroon als `assertRowsWritten` in
 * `src/features/laadplein-designer/hooks/useScenarioEnergyLink.ts:135`.
 *
 * Meer dan één rij is net zo fout als nul: elke schrijfactie in dit scherm
 * filtert op één `id`.
 */
export function telGeraakteRijen(rijen: unknown, wat: string, verwacht = 1, extraUitleg = ''): void {
  const aantal = Array.isArray(rijen) ? rijen.length : 0;
  if (aantal === verwacht) return;
  throw new NietsGeschreven(
    `${wat} is NIET opgeslagen: de database gaf ${aantal} rijen terug in plaats van ${verwacht}. `
    + 'Een schrijfactie die door de rechten wordt tegengehouden raakt nul rijen zonder een fout te '
    + 'geven — controleer of je account dit mag, en of de rij nog bestaat.'
    + (extraUitleg ? ` ${extraUitleg}` : ''),
  );
}

/**
 * Wat erbij staat als de UPDATE óók op `updated_at` filterde.
 *
 * Zonder die filter overschrijft de laatste schrijver de eerste zonder dat
 * iemand het merkt — en erger: de voorvertoning die je zag was dan gerekend op
 * een stand die inmiddels niet meer bestond. Het bedrag dat op de knop stond
 * klopte dus niet meer.
 */
export const BOTSING_UITLEG =
  'Let op: deze wijziging filterde ook op het tijdstip van de vorige wijziging. Nul rijen betekent '
  + 'waarschijnlijk dat iemand anders deze rij zojuist heeft aangepast — en dan klopte het bedrag in de '
  + 'voorvertoning ook niet meer. Ververs de pagina en bekijk het opnieuw.';

/**
 * Duidt een fout uit een schrijfactie, zodat het scherm iets anders kan tonen
 * dan de ruwe Postgres-tekst.
 *
 * De vier gevallen die er echt toe doen:
 *  - de voorvertoning is niet bevestigd. Geen storing en geen weigering van de
 *    database: wij hebben met opzet niet geschreven, en dat moet als zodanig
 *    in beeld komen — niet als "mislukt";
 *  - het identiteitsslot uit 083 gooit 42501 mét `tabel.kolom` in de melding;
 *  - RLS weigert een rij met 42501 en de tekst "row-level security policy";
 *  - onze eigen `NietsGeschreven`: geen fout van de database, maar stilte.
 */
export function duidSchrijffout(error: unknown, wat: string): Schrijffout {
  if (error instanceof NietsGeschreven) {
    return { soort: 'niets-geschreven', kolom: null, melding: error.message };
  }
  // Los van de klasse herkend: `subsidie-voorvertoning.ts` is een eigen module
  // en een instanceof-toets zou breken zodra er twee kopieën van geladen zijn.
  if (error && typeof error === 'object' && (error as { soort?: unknown }).soort === 'niet-bevestigd') {
    return { soort: 'niet-bevestigd', kolom: null, melding: leesMelding(error) };
  }

  const code = leesCode(error);
  const tekst = leesMelding(error);

  if (code === '42501') {
    const kolom = kolomUitMelding(tekst);
    if (kolom) {
      return {
        soort: 'kolom-op-slot',
        kolom,
        melding: `${wat} is geweigerd: ${kolom} mag je niet wijzigen. ${tekst}`,
      };
    }
    return {
      soort: 'rij-geweigerd',
      kolom: null,
      melding:
        `${wat} is geweigerd: je account mag deze rij niet schrijven. `
        + 'Schrijven op de subsidiedata is voorbehouden aan admin, super_admin en crm_user, '
        + 'en verwijderen alleen aan admin en super_admin.',
    };
  }

  if (code === 'PGRST204' || code === 'PGRST205' || code === '42P01' || code === '42703') {
    return {
      soort: 'niet-toegepast',
      kolom: kolomUitMelding(tekst),
      melding:
        `${wat} kon niet: de database kent deze tabel of kolom niet. `
        + 'Waarschijnlijk is migratie 083 nog niet toegepast. ' + tekst,
    };
  }

  return { soort: 'onbekend', kolom: kolomUitMelding(tekst), melding: `${wat} mislukt: ${tekst}` };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Weten we ZEKER dat er niets is geschreven?
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * SQLSTATE is vijf tekens, cijfers en hoofdletters. `ECONNRESET`, `ETIMEDOUT`
 * en `ERR_NETWORK` vallen daar niet onder, en dat is precies de bedoeling.
 */
const SQLSTATE = /^[0-9A-Z]{5}$/;

/**
 * Mag de eenmalige bevestiging na deze fout weer worden vrijgegeven?
 *
 * DIT IS EEN VEILIGHEIDSGRENS, geen gemak. De vraag is niet "is het misgegaan"
 * maar "weten we zeker dat er NIETS is geschreven". Bij een netwerk- of
 * timeoutfout weten we dat niet: het statement kan Postgres wél hebben bereikt
 * en de rij kan er staan. Geef je de sleutel dan vrij, dan kan een tweede klik
 * een tweede rij maken — en een duplicaat-tarief tilt intake 6daab7ab van
 * EUR 212.000 naar EUR 270.000.
 *
 * Zeker weten we het in drie gevallen:
 *  - onze eigen grendel sloeg toe (`niet-bevestigd`): Supabase is niet eens
 *    aangeraakt;
 *  - `NietsGeschreven`: het statement liep, en raakte nul rijen;
 *  - de database gaf een SQLSTATE of een PostgREST-code terug: dan heeft de
 *    server het verzoek ontvangen, beoordeeld en afgewezen.
 *
 * Alles daarbuiten — geen code, een netwerkcode, een afgebroken verbinding —
 * telt als ONZEKER, en dan blijft de sleutel verbruikt.
 */
export function isZekerNietsGeschreven(error: unknown): boolean {
  if (error instanceof NietsGeschreven) return true;
  if (error && typeof error === 'object') {
    const soort = (error as { soort?: unknown }).soort;
    if (soort === 'niet-bevestigd' || soort === 'niets-geschreven') return true;
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string' && (SQLSTATE.test(code) || code.startsWith('PGRST'))) return true;
  }
  return false;
}

/**
 * Wat het scherm zegt als een schrijfactie faalde zónder dat we weten of hij is
 * aangekomen. Dan is "probeer opnieuw" het verkeerde advies.
 */
export const ONZEKER_UITLEG =
  'We weten niet of deze wijziging de database heeft bereikt: de verbinding brak af vóór er een antwoord '
  + 'kwam. Ververs de pagina en kijk of hij er al staat. Klik hem niet nog een keer weg — bij een nieuw '
  + 'tarief zou dat een tweede rij kunnen maken.';

/* ──────────────────────────────────────────────────────────────────────────
 * De versiefilter op een UPDATE
 * ────────────────────────────────────────────────────────────────────────── */

/** Het stukje van een PostgREST-querybouwer dat we hier nodig hebben. */
export interface FilterbareQuery {
  eq(kolom: string, waarde: string): unknown;
}

/**
 * Filtert een UPDATE óók op de versie die de voorvertoning zag.
 *
 * Staat hier en niet in `useSubsidieBeheer` zodat de test hem met een neppe
 * querybouwer kan AANROEPEN in plaats van de brontekst te grepen. Dat is het
 * verschil dat ertoe deed: een grep op de bron bleef groen toen de hele body
 * door `return query;` werd vervangen.
 *
 * Lege of ontbrekende `gezienOp` betekent "geen versie bekend" — een INSERT, of
 * een stand van vóór migratie 083 waarin `updated_at` nog niet bestaat. Dan
 * geen filter, want `.eq('updated_at', null)` zou nul rijen raken en elke
 * schrijfactie laten stranden.
 */
export function opVersie<T extends FilterbareQuery>(query: T, gezienOp: string | null | undefined): T {
  return typeof gezienOp === 'string' && gezienOp.length > 0
    ? (query.eq('updated_at', gezienOp) as T)
    : query;
}

/**
 * Wat de banner bovenaan het beheerpaneel zegt, gegeven de rechten. Drie
 * gevallen, en geen ervan liegt: alles mogen, een deel mogen, niets mogen.
 *
 * Hij staat hier en niet in de JSX omdat hij de enige tekst is die de hele
 * grens in één alinea samenvat — en omdat een banner die iets anders belooft
 * dan de knoppen doen, erger is dan geen banner. De test legt hem naast
 * `schrijfrechtenVoorRol`.
 */
export function bannerVoorRechten(
  rechten: Schrijfrechten,
  stand: SchemaStand,
): { toon: boolean; kop: string; uitleg: string } {
  if (!rechten.ietsSchrijven) {
    return {
      toon: true,
      kop: 'Je kunt hier meekijken maar niet wijzigen.',
      uitleg: REDEN.geenSchrijfrecht,
    };
  }

  /**
   * De stand van vóór 083, en die hoort er te staan zolang hij waar is. Eerder
   * beloofde deze banner een crm_user "je mag alles behalve een hele regeling
   * weggooien" terwijl élke regel- en bedrag-actie stil op nul rijen strandde
   * en vijf regelingvelden met 42501 terugkwamen.
   */
  if (!stand.migratie083 && !rechten.regelVerwijderen) {
    return {
      toon: true,
      kop: 'Migratie 083 staat nog niet in de database: je mag vandaag minder dan dit scherm kan.',
      uitleg:
        'Van een regeling kun je nu de bronstand bijwerken — naam, status, data, budgetten, de RVO-pagina '
        + 'en de controledatum. Het soort, de percentages en de grenzen staan uit, en regels en bedragen '
        + 'kun je nog helemaal niet wijzigen of toevoegen: die rechten komen pas met 083. Vraag een '
        + 'beheerder om de migratie toe te passen.',
    };
  }

  if (rechten.regelingVerwijderen) {
    return { toon: false, kop: '', uitleg: '' };
  }

  return {
    toon: true,
    kop: 'Je mag alles behalve een hele regeling weggooien.',
    uitleg:
      'Elk inhoudelijk veld van een regeling, een regel en een bedrag staat voor je open, en je mag regels '
      + 'en bedragen toevoegen én verwijderen. Verschuift een wijziging een bedrag of een beoordeling in een '
      + 'lopend dossier, dan zie je eerst wélk dossier van welk bedrag naar welk bedrag gaat, en pas na je '
      + 'bevestiging wordt er opgeslagen. Alleen een hele regeling verwijderen blijft bij een beheerder: '
      + 'daar gaan alle regels en bedragen eronder in mee.',
  };
}
